import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaWhatsapp,
  FaPhoneAlt,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import { HiPlus } from "react-icons/hi";

// ============================================================
// CONTACT OPTIONS
// ============================================================

const contacts = [
  {
    icon: FaMapMarkerAlt,
    label: "Location",
    bg: "bg-gradient-to-br from-red-500 to-red-600",
    href: "https://maps.app.goo.gl/c3yH2WpbK4sFTjoW8",
    external: true,
  },
  {
    icon: MdEmail,
    label: "Email",
    bg: "bg-gradient-to-br from-yellow-300 to-yellow-400",
    href: "mailto:web.bocket@gmail.com",
    external: false,
  },
  {
    icon: FaPhoneAlt,
    label: "Call",
    bg: "bg-gradient-to-br from-blue-500 to-blue-600",
    href: "tel:+917008065931",
    external: false,
  },
  {
    icon: FaWhatsapp,
    label: "WhatsApp",
    bg: "bg-gradient-to-br from-green-500 to-green-600",
    href: "https://wa.me/917008065931",
    external: true,
  },
];

// ============================================================
// FLOATING CONTACT
// ============================================================

export default function FloatingContact() {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="
        fixed
        z-[9999]

        /* =====================================================
           MOBILE
        ====================================================== */
        bottom-4
        left-3

        /* =====================================================
           SMALL MOBILE
        ====================================================== */
        sm:bottom-5
        sm:left-4

        /* =====================================================
           TABLET
        ====================================================== */
        md:bottom-7
        md:left-5

        /* =====================================================
           DESKTOP
        ====================================================== */
        lg:bottom-8
        lg:left-6

        /* =====================================================
           LARGE DESKTOP
        ====================================================== */
        xl:bottom-10
        xl:left-7
      "
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <div
        className="
          relative
          flex
          flex-col
          items-center
        "
      >
        {/* =====================================================
            CONTACT OPTIONS
        ====================================================== */}

        <AnimatePresence>
          {open && (
            <motion.div
              initial="hidden"
              animate="show"
              exit="hidden"
              variants={{
                hidden: {},
                show: {
                  transition: {
                    staggerChildren: 0.055,
                    delayChildren: 0.03,
                  },
                },
              }}
              className="
                mb-2.5
                flex
                flex-col
                items-center
                gap-2.5

                sm:mb-3
                sm:gap-3

                md:mb-3.5
                md:gap-3.5

                lg:mb-4
                lg:gap-4
              "
            >
              {contacts.map((item, index) => {
                const Icon = item.icon;

                return (
                  <motion.a
                    key={item.label}
                    href={item.href}
                    target={item.external ? "_blank" : undefined}
                    rel={
                      item.external
                        ? "noopener noreferrer"
                        : undefined
                    }
                    aria-label={item.label}
                    variants={{
                      hidden: {
                        opacity: 0,
                        y: 12,
                        scale: 0.75,
                      },
                      show: {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                      },
                    }}
                    transition={{
                      duration: 0.28,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    whileHover={{
                      scale: 1.08,
                      y: -2,
                    }}
                    whileTap={{
                      scale: 0.94,
                    }}
                    className="
                      group
                      relative
                      flex
                      items-center
                      justify-center
                      outline-none
                    "
                  >
                    {/* =================================================
                        LABEL
                        Visible on hover for desktop/tablet.
                        Hidden on smaller touch screens to avoid overflow.
                    ================================================== */}

                    <span
                      className="
                        pointer-events-none
                        absolute
                        left-full
                        top-1/2
                        ml-2.5
                        -translate-y-1/2
                        translate-x-1
                        whitespace-nowrap
                        rounded-full
                        border
                        border-white/[0.08]
                        bg-black/90
                        px-2.5
                        py-1.5
                        text-[10px]
                        font-medium
                        tracking-wide
                        text-white
                        opacity-0
                        shadow-[0_8px_25px_rgba(0,0,0,0.35)]
                        backdrop-blur-xl
                        transition-all
                        duration-300
                        group-hover:translate-x-0
                        group-hover:opacity-100

                        /* Mobile */
                        hidden

                        /* Small devices */
                        sm:hidden

                        /* Tablet */
                        md:block
                        md:text-[11px]

                        /* Desktop */
                        lg:ml-3
                        lg:px-3
                        lg:py-1.5
                        lg:text-xs
                      "
                    >
                      {item.label}
                    </span>

                    {/* =================================================
                        ICON WRAPPER
                    ================================================== */}

                    <div
                      className={`
                        relative
                        flex
                        items-center
                        justify-center
                        overflow-hidden
                        rounded-full
                        ${item.bg}

                        /* Mobile */
                        h-10
                        w-10

                        /* Small */
                        sm:h-11
                        sm:w-11

                        /* Tablet */
                        md:h-12
                        md:w-12

                        /* Desktop */
                        lg:h-[50px]
                        lg:w-[50px]

                        /* Large */
                        xl:h-[52px]
                        xl:w-[52px]

                        shadow-[0_8px_24px_rgba(0,0,0,0.30)]
                        ring-1
                        ring-white/10
                        transition-all
                        duration-300
                        group-hover:ring-white/20
                        group-hover:shadow-[0_12px_30px_rgba(0,0,0,0.40)]
                      `}
                    >
                      {/* Soft shine */}
                      <span
                        className="
                          pointer-events-none
                          absolute
                          -left-1/2
                          top-0
                          h-full
                          w-1/2
                          rotate-12
                          bg-white/20
                          blur-md
                          transition-all
                          duration-700
                          group-hover:left-[120%]
                        "
                      />

                      <Icon
                        className="
                          relative
                          z-10
                          text-[15px]
                          text-white

                          sm:text-[17px]

                          md:text-[19px]

                          lg:text-[20px]

                          xl:text-[21px]
                        "
                      />
                    </div>
                  </motion.a>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>

        {/* =====================================================
            MAIN FLOATING BUTTON
        ====================================================== */}

        <motion.button
          type="button"
          aria-label={open ? "Close contact options" : "Open contact options"}
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
          whileHover={{
            scale: 1.07,
          }}
          whileTap={{
            scale: 0.93,
          }}
          className="
            group
            relative
            flex
            items-center
            justify-center
            overflow-hidden
            rounded-full
            border
            border-white/10
            bg-gradient-to-br
            from-[#F7931E]
            to-[#E76A17]

            /* Mobile */
            h-11
            w-11

            /* Small */
            sm:h-12
            sm:w-12

            /* Tablet */
            md:h-[50px]
            md:w-[50px]

            /* Desktop */
            lg:h-[54px]
            lg:w-[54px]

            /* Large */
            xl:h-[56px]
            xl:w-[56px]

            shadow-[0_10px_30px_rgba(247,147,30,0.28)]
            outline-none
            transition-all
            duration-300

            hover:shadow-[0_14px_40px_rgba(247,147,30,0.40)]

            focus-visible:ring-2
            focus-visible:ring-[#F7931E]/60
            focus-visible:ring-offset-2
            focus-visible:ring-offset-black
          "
        >
          {/* =====================================================
              BUTTON INNER GLOW
          ====================================================== */}

          <span
            className="
              pointer-events-none
              absolute
              inset-0
              rounded-full
              bg-white/10
              opacity-0
              transition-opacity
              duration-300
              group-hover:opacity-100
            "
          />

          {/* =====================================================
              ROTATING PLUS
          ====================================================== */}

          <motion.div
            animate={{
              rotate: open ? 45 : 0,
            }}
            transition={{
              duration: 0.3,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="
              relative
              z-10
              flex
              items-center
              justify-center
            "
          >
            <HiPlus
              className="
                text-[18px]
                text-white

                sm:text-[19px]

                md:text-[20px]

                lg:text-[21px]

                xl:text-[22px]
              "
            />
          </motion.div>
        </motion.button>

        {/* =====================================================
            SUBTLE PULSE RING
        ====================================================== */}

        {!open && (
          <motion.span
            className="
              pointer-events-none
              absolute
              inset-0
              rounded-full
              border
              border-[#F7931E]/30
            "
            animate={{
              scale: [1, 1.18, 1],
              opacity: [0.45, 0, 0.45],
            }}
            transition={{
              duration: 2.8,
              repeat: Infinity,
              ease: "easeOut",
            }}
          />
        )}
      </div>
    </div>
  );
}